import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import PropTypes from 'prop-types'
import {
  completeNewPassword as completeNewPasswordWithCognito,
  getCurrentPrincipal,
  signIn as signInWithCognito,
  signOut as signOutFromCognito
} from './cognito'

import { getSharingDirectory } from '../service/rest'

const AuthContext = createContext(null)

const AUTH_LOADING = 'loading'
const AUTH_SIGNED_OUT = 'signedOut'
const AUTH_AUTHENTICATED = 'authenticated'
const AUTH_NEW_PASSWORD = 'newPasswordRequired'

const AuthProvider = ({ children }) => {
  const [status, setStatus] = useState(AUTH_LOADING)
  const [principal, setPrincipal] = useState(null)
  const [sharingGroups, setSharingGroups] = useState(null)
  const refreshSharing = useCallback(async () => {
    const directory = await getSharingDirectory()
    setSharingGroups(directory.groups)
    return directory
  }, [])
  useEffect(() => {
    let active = true
    setSharingGroups(null)
    if (status === AUTH_AUTHENTICATED) {
      getSharingDirectory().then(d => { if (active) setSharingGroups(d.groups) }).catch(() => {
        if (active) setSharingGroups([])
      })
    }
    return () => { active = false }
  }, [principal?.username, status])

  const refresh = useCallback(async ({ forceRefresh = false } = {}) => {
    setStatus(AUTH_LOADING)
    const current = await getCurrentPrincipal({ forceRefresh })
    setPrincipal(current)
    setStatus(current ? AUTH_AUTHENTICATED : AUTH_SIGNED_OUT)
    return current
  }, [])

  useEffect(() => {
    refresh({ forceRefresh: true })
  }, [refresh])

  const signIn = useCallback(async (username, password) => {
    const result = await signInWithCognito(username, password)
    if (result.type === AUTH_NEW_PASSWORD) {
      setPrincipal(null)
      setStatus(AUTH_NEW_PASSWORD)
      return result
    }

    setPrincipal(result.principal)
    setStatus(AUTH_AUTHENTICATED)
    return result
  }, [])

  const completeNewPassword = useCallback(async newPassword => {
    const current = await completeNewPasswordWithCognito(newPassword)
    setPrincipal(current)
    setStatus(AUTH_AUTHENTICATED)
    return current
  }, [])

  const signOut = useCallback(() => {
    signOutFromCognito()
    setPrincipal(null)
    setStatus(AUTH_SIGNED_OUT)
  }, [])

  const value = useMemo(() => ({
    status,
    principal,
    user: principal ? { username: principal.username } : null,
    groups: sharingGroups ? sharingGroups.filter(g => g.role !== 'viewer').map(g => g.name) : [],
    refreshSharing,
    signIn,
    completeNewPassword,
    signOut,
    refresh,
    isAuthenticated: status === AUTH_AUTHENTICATED
  }), [completeNewPassword, principal, refresh, signIn, signOut, status, sharingGroups, refreshSharing])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
}

const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider')
  }
  return context
}

export {
  AUTH_AUTHENTICATED,
  AUTH_LOADING,
  AUTH_NEW_PASSWORD,
  AUTH_SIGNED_OUT,
  AuthProvider,
  useAuth
}
